import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, AlertTriangle, Package, ClipboardList, CheckCircle, XCircle, Clock, ArrowDownCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 1 bag = 40 KG
const KG_PER_BAG = 40;

// Helper function to convert bags to KG
const bagsToKg = (bags) => bags * KG_PER_BAG;

export default function RawMaterials({ user }) {
  const [materials, setMaterials] = useState([]);
  const [usageRequests, setUsageRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [activeTab, setActiveTab] = useState('inventory');
  
  const [formData, setFormData] = useState({
    material_name: '',
    material_code: '',
    quantity: 0,
    unit: 'bags',
    reorder_level: 0,
    supplier_name: '',
    unit_price: 0
  });

  const [usageFormData, setUsageFormData] = useState({
    material_id: '',
    material_name: '',
    quantity_requested: 0,
    unit: '',
    purpose: '',
    production_batch: '',
    notes: ''
  });

  useEffect(() => {
    fetchMaterials();
    fetchUsageRequests();
  }, []);

  const fetchMaterials = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterials(response.data);
    } catch (error) {
      toast.error('Failed to fetch materials');
    }
  };

  const fetchUsageRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/material-usage-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch usage requests');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingMaterial) {
        await axios.put(`${API}/raw-materials/${editingMaterial.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Material updated successfully');
      } else {
        await axios.post(`${API}/raw-materials`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Material added successfully');
      }
      setOpen(false);
      resetForm();
      fetchMaterials();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleUsageRequest = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/material-usage-requests`, usageFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usage request submitted for approval');
      setUsageOpen(false);
      resetUsageForm();
      fetchUsageRequests();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleApprove = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/material-usage-requests/${requestId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request approved - Material deducted from stock');
      fetchUsageRequests();
      fetchMaterials();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleReject = async (requestId) => {
    const reason = prompt('Enter rejection reason (optional):');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/material-usage-requests/${requestId}/reject?rejection_reason=${encodeURIComponent(reason || '')}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request rejected');
      fetchUsageRequests();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const resetForm = () => {
    setFormData({
      material_name: '',
      material_code: '',
      quantity: 0,
      unit: 'kg',
      reorder_level: 0,
      supplier_name: '',
      unit_price: 0
    });
    setEditingMaterial(null);
  };

  const resetUsageForm = () => {
    setUsageFormData({
      material_id: '',
      material_name: '',
      quantity_requested: 0,
      unit: '',
      purpose: '',
      production_batch: '',
      notes: ''
    });
    setSelectedMaterial(null);
  };

  const openEditDialog = (material) => {
    setEditingMaterial(material);
    setFormData({
      material_name: material.material_name,
      material_code: material.material_code,
      quantity: material.quantity,
      unit: material.unit,
      reorder_level: material.reorder_level,
      supplier_name: material.supplier_name || '',
      unit_price: material.unit_price
    });
    setOpen(true);
  };

  const openUsageDialog = (material) => {
    setSelectedMaterial(material);
    setUsageFormData({
      material_id: material.id,
      material_name: material.material_name,
      quantity_requested: 0,
      unit: material.unit,
      purpose: '',
      production_batch: '',
      notes: ''
    });
    setUsageOpen(true);
  };

  // Calculate totals
  const totalMaterials = materials.length;
  const lowStockMaterials = materials.filter(m => m.quantity <= m.reorder_level).length;
  const pendingRequests = usageRequests.filter(r => r.status === 'pending').length;
  const approvedRequests = usageRequests.filter(r => r.status === 'approved');
  const totalUsed = approvedRequests.reduce((sum, r) => sum + r.quantity_requested, 0);

  const canApprove = user?.role === 'admin' || user?.role === 'finance' || user?.role === 'inventory';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Raw Materials Inventory</h1>
          <p className="text-gray-600">Manage raw materials and track usage for production</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Materials</p>
                <p className="text-2xl font-bold">{totalMaterials}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 p-3 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold text-red-600">{lowStockMaterials}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Approvals</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingRequests}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <ArrowDownCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Used</p>
                <p className="text-2xl font-bold text-green-600">{totalUsed.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="inventory" className="flex items-center gap-2">
            <Package size={16} /> Inventory
          </TabsTrigger>
          <TabsTrigger value="usage" className="flex items-center gap-2">
            <ClipboardList size={16} /> Usage Requests
            {pendingRequests > 0 && (
              <Badge className="bg-yellow-500 text-white ml-1">{pendingRequests}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="mt-4">
          <div className="flex justify-end mb-4">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-material-button" onClick={resetForm} className="bg-[#8FC043] hover:bg-[#7aa936]">
                  <Plus size={16} className="mr-2" />
                  Add Material
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingMaterial ? 'Edit Material' : 'Add New Material'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="material_name">Material Name *</Label>
                      <Input
                        id="material_name"
                        data-testid="material-name-input"
                        value={formData.material_name}
                        onChange={(e) => setFormData({ ...formData, material_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="material_code">Material Code *</Label>
                      <Input
                        id="material_code"
                        data-testid="material-code-input"
                        value={formData.material_code}
                        onChange={(e) => setFormData({ ...formData, material_code: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="quantity">Quantity *</Label>
                      <Input
                        id="quantity"
                        data-testid="quantity-input"
                        type="number"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="unit">Unit *</Label>
                      <select
                        id="unit"
                        data-testid="unit-input"
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        required
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        <option value="bags">Bags (1 bag = 40 kg)</option>
                        <option value="kg">Kilograms (kg)</option>
                        <option value="liters">Liters</option>
                        <option value="pieces">Pieces</option>
                        <option value="boxes">Boxes</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reorder_level">Reorder Level *</Label>
                      <Input
                        id="reorder_level"
                        data-testid="reorder-level-input"
                        type="number"
                        value={formData.reorder_level}
                        onChange={(e) => setFormData({ ...formData, reorder_level: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="supplier_name">Supplier Name</Label>
                      <Input
                        id="supplier_name"
                        data-testid="supplier-name-input"
                        value={formData.supplier_name}
                        onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="unit_price">Unit Price (Tsh) *</Label>
                      <Input
                        id="unit_price"
                        data-testid="unit-price-input"
                        type="number"
                        value={formData.unit_price}
                        onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </div>
                  </div>
                  <Button data-testid="save-material-button" type="submit" className="w-full">
                    {editingMaterial ? 'Update Material' : 'Add Material'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Material</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Current Stock</TableHead>
                  <TableHead>Equivalent (KG)</TableHead>
                  <TableHead>Reorder Level</TableHead>
                  <TableHead>Unit Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                      No materials found. Add your first material to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  materials.map((material) => {
                    const isLowStock = material.quantity <= material.reorder_level;
                    const isBags = material.unit === 'bags';
                    const kgEquivalent = isBags ? bagsToKg(material.quantity) : material.quantity;
                    return (
                      <TableRow key={material.id} className={isLowStock ? 'bg-red-50' : ''}>
                        <TableCell className="font-medium">{material.material_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{material.material_code}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className={isLowStock ? 'text-red-600 font-bold' : 'font-semibold'}>
                            {material.quantity} {material.unit}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-gray-600">
                            {isBags ? `${kgEquivalent.toLocaleString()} kg` : '-'}
                          </span>
                        </TableCell>
                        <TableCell>{material.reorder_level} {material.unit}</TableCell>
                        <TableCell>Tsh {material.unit_price.toLocaleString()}/{material.unit === 'bags' ? 'bag' : material.unit}</TableCell>
                        <TableCell>
                          {isLowStock ? (
                            <Badge className="bg-red-100 text-red-800">
                              <AlertTriangle size={12} className="mr-1" /> Low Stock
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">In Stock</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openUsageDialog(material)}
                            className="text-blue-600 hover:text-blue-800 mr-2"
                            title="Request Material Usage"
                          >
                            <ArrowDownCircle size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(material)}
                          >
                            <Pencil size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Usage Requests Tab */}
        <TabsContent value="usage" className="mt-4">
          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Material</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Requested By</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Approved By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usageRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                      No usage requests found.
                    </TableCell>
                  </TableRow>
                ) : (
                  usageRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="font-medium">{request.material_name}</TableCell>
                      <TableCell>{request.quantity_requested} {request.unit}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{request.purpose}</TableCell>
                      <TableCell>{request.requested_by_name}</TableCell>
                      <TableCell>
                        {request.status === 'pending' && (
                          <Badge className="bg-yellow-100 text-yellow-800">
                            <Clock size={12} className="mr-1" /> Pending
                          </Badge>
                        )}
                        {request.status === 'approved' && (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle size={12} className="mr-1" /> Approved
                          </Badge>
                        )}
                        {request.status === 'rejected' && (
                          <Badge className="bg-red-100 text-red-800">
                            <XCircle size={12} className="mr-1" /> Rejected
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {request.approved_by_name || '-'}
                        {request.rejection_reason && (
                          <p className="text-xs text-red-600">{request.rejection_reason}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {request.status === 'pending' && canApprove && (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApprove(request.id)}
                            >
                              <CheckCircle size={14} className="mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReject(request.id)}
                            >
                              <XCircle size={14} className="mr-1" /> Reject
                            </Button>
                          </div>
                        )}
                        {request.status === 'pending' && !canApprove && (
                          <span className="text-sm text-gray-500">Awaiting approval</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Usage Request Dialog */}
      <Dialog open={usageOpen} onOpenChange={setUsageOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Material for Production</DialogTitle>
          </DialogHeader>
          {selectedMaterial && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Material:</strong> {selectedMaterial.material_name}
              </p>
              <p className="text-sm text-blue-800">
                <strong>Available Stock:</strong> {selectedMaterial.quantity} {selectedMaterial.unit}
                {selectedMaterial.unit === 'bags' && (
                  <span className="text-blue-600 ml-2">
                    ({bagsToKg(selectedMaterial.quantity).toLocaleString()} kg)
                  </span>
                )}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Note: 1 bag = {KG_PER_BAG} kg
              </p>
            </div>
          )}
          <form onSubmit={handleUsageRequest} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity_requested">Quantity ({selectedMaterial?.unit || 'bags'}) *</Label>
                <Input
                  id="quantity_requested"
                  type="number"
                  value={usageFormData.quantity_requested}
                  onChange={(e) => setUsageFormData({ ...usageFormData, quantity_requested: parseFloat(e.target.value) || 0 })}
                  required
                  max={selectedMaterial?.quantity}
                  step="1"
                  min="1"
                />
                <p className="text-xs text-gray-500">
                  Max: {selectedMaterial?.quantity} {selectedMaterial?.unit}
                  {selectedMaterial?.unit === 'bags' && usageFormData.quantity_requested > 0 && (
                    <span className="text-green-600 ml-2">
                      = {bagsToKg(usageFormData.quantity_requested).toLocaleString()} kg
                    </span>
                  )}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="production_batch">Production Batch #</Label>
                <Input
                  id="production_batch"
                  value={usageFormData.production_batch}
                  onChange={(e) => setUsageFormData({ ...usageFormData, production_batch: e.target.value })}
                  placeholder="e.g., BATCH-2026-001"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="purpose">Purpose / Reason *</Label>
              <Textarea
                id="purpose"
                value={usageFormData.purpose}
                onChange={(e) => setUsageFormData({ ...usageFormData, purpose: e.target.value })}
                required
                placeholder="e.g., Production of white chalk cartons"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={usageFormData.notes}
                onChange={(e) => setUsageFormData({ ...usageFormData, notes: e.target.value })}
                placeholder="Any additional information..."
                rows={2}
              />
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg">
              <p className="text-sm text-yellow-800">
                <AlertTriangle size={14} className="inline mr-1" />
                This request will be sent to Finance/Inventory Coordinator for approval before materials are deducted from stock.
              </p>
            </div>
            <Button type="submit" className="w-full bg-[#8FC043] hover:bg-[#7aa936]">
              Submit Request for Approval
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
